import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  BackendService,
  Base,
  TypeOrmFindManyOptions,
  UpdateResult,
} from '@wilson/base';
import { ConfigOptions, ConfigService } from '@wilson/config';
import { Job, RequestForProposal } from '@wilson/interfaces';
import { CreateJob } from '@wilson/share/interfaces';
import { stringify } from 'qs';
import { firstValueFrom, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class JobGateway {
  protected readonly http = inject(HttpClient);
  protected readonly config = inject<ConfigOptions>(ConfigService);

  protected readonly path = 'jobs';

  public createJob(payload: CreateJob): Observable<(CreateJob & Base)[]> {
    return this.http.post<(CreateJob & Base)[]>(
      `${this.config.host}/${this.path}`,
      {
        items: [payload],
      },
    );
  }

  public get(
    entityId: Job['id'] | null,
    options?: TypeOrmFindManyOptions,
  ): Observable<Job> {
    const params = stringify(options);
    return this.http.get<Job>(
      `${this.config.host}/${this.path}/${entityId}?${params}`,
    );
  }

  public getAll(
    options?: TypeOrmFindManyOptions,
    {
      headers,
    }: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
    } = {},
  ): Observable<Job[]> {
    const params = stringify(options, {
      arrayFormat: 'brackets',
    });

    return this.http.get<Job[]>(`${this.config.host}/${this.path}?${params}`, {
      headers,
    });
  }
  public update(entity: Partial<Job>): Promise<UpdateResult> {
    return firstValueFrom(
      this.http.patch<UpdateResult>(
        `${this.config.host}/${this.path}/${entity.id}`,
        entity,
      ),
    );
  }

  public updateClientProposalResponse(
    jobId: string,
    requestForProposalId: string,
    payload: Partial<RequestForProposal>,
  ): Promise<Partial<RequestForProposal>> {
    return firstValueFrom(
      this.http.patch<Partial<RequestForProposal>>(
        `${this.config.host}/${this.path}/${jobId}/requests-for-proposals/${requestForProposalId}`,
        payload,
      ),
    );
  }

  public withdrawJob(id: string): Observable<Job> {
    return this.http.post<Job>(
      `${this.config.host}/${this.path}/${id}/withdraw`,
      {},
    );
  }

  public cancelActivities(ids: string[], jobId: string) {
    return firstValueFrom(
      this.http.patch<Partial<RequestForProposal>>(
        `${this.config.host}/${this.path}/${jobId}/cancel-activities`,
        { activityIds: ids },
      ),
    );
  }
}
